import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChatRoom, ChatRoomType } from "@/models/chat.model";
import { useMemo, useState } from "react";
import { useAppSelector } from "@/lib/store";
import { getInitials } from "@/lib/utils.ts";
import { UserPlus, Users, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router";
import { organization_type } from "@/models/organization.model";
import AddToGroupModal from "./add-to-group-modal";
import MembersSidebar from "./members-sidebar";

interface Props { room: ChatRoom; }

export default function ChatHeader({ room }: Props) {
    const { user: me } = useAppSelector((state) => state.auth);
    const users = useAppSelector(s => s.users);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showMembersSidebar, setShowMembersSidebar] = useState(false);
    
    const activeOrg = useAppSelector((s) => {
        const activeOrgId = s.organization.activeOrgId;
        return s.organization.organizations.find(org => org.id === activeOrgId);
    });
    
    // Determine appointment route based on organization type
    const getAppointmentRoute = (appointmentId: string): string | null => {
        if (!activeOrg) return null;
        
        switch (activeOrg.type) {
            case organization_type.pmc:
            case organization_type.vendor:
            case organization_type.subs:
            case organization_type.emp:
                return `/appointments/${appointmentId}`;
            default:
                return null;
        }
    };

    const header = useMemo(() => {
        if (room.type === ChatRoomType.direct) {
            const otherId = room.users.find(u => u !== me?.id)!;
            const other   = users[otherId];

            return {
                avatar: other?.image ?? null,
                initials: getInitials(other?.name || '??'),
                title: other?.name || 'Unknown',
                subtitle: 'Direct message',
                isGroupChat: false,
            };
        }

        if (room.type === ChatRoomType.daily_work && room.daily_work_data) {
            const propertyName = room.daily_work_data.property?.name ?? "Daily Work";
            const title = `${propertyName} (DW)`;

            return {
                avatar: null,
                initials: getInitials(propertyName),
                title,
                subtitle: `${room.users.length} members`,
                isGroupChat: true,
            };
        }

        // Appointment group chat
        const appointmentData = room.appointment_data;
        const title = appointmentData 
            ? `${appointmentData.property.name} ${appointmentData.unit.unit_number}`
            : 'Appointment Chat';
        const subtitle = appointmentData
            ? `${appointmentData.service.name} • ${room.users.length} members`
            : `${room.users.length} members`;
            
        return {
            avatar: null,
            initials: appointmentData?.property.name.slice(0, 2).toUpperCase() || 'AC',
            title,
            subtitle,
            isGroupChat: true,
        };
    }, [room.id, me?.id, room.users.length, room.appointment_data, room.daily_work_data, users]);

    return (
        <>
            <div className="flex items-center justify-between w-full">
                <div className="flex items-start space-x-3">
                    <Avatar key={header.avatar} className="h-10 w-10 rounded-sm">
                        {header.avatar && <AvatarImage src={header.avatar} />}
                        <AvatarFallback>{header.initials}</AvatarFallback>
                    </Avatar>
                    <div>
                        <h2 className="text-sm font-medium text-foreground">{header.title}</h2>
                        {header.isGroupChat ? (
                            <button 
                                onClick={() => setShowMembersSidebar(true)}
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                            >
                                <Users className="h-3 w-3" />
                                {header.subtitle}
                            </button>
                        ) : (
                            <p className="text-xs text-muted-foreground">{header.subtitle}</p>
                        )}
                    </div>
                </div>
                
                {header.isGroupChat && room.appointment && (
                    <div className="ml-auto flex items-center gap-2">
                        {getAppointmentRoute(room.appointment) && (
                            <Link to={getAppointmentRoute(room.appointment)!}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                >
                                    <LinkIcon className="h-4 w-4 mr-1" />
                                    Appointment
                                </Button>
                            </Link>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAddModal(true)}
                        >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add
                        </Button>
                    </div>
                )}
            </div>

            {room.appointment && (
                <AddToGroupModal
                    open={showAddModal}
                    onOpenChange={setShowAddModal}
                    roomId={room.id}
                    currentUsers={room.users}
                />
            )}
            
            {header.isGroupChat && (
                <MembersSidebar
                    open={showMembersSidebar}
                    onOpenChange={setShowMembersSidebar}
                    memberCount={room.users.length}
                    roomId={room.id}
                />
            )}
        </>
    );
}